import React, { useRef, useState } from 'react';
import { Box, Chip, ClickAwayListener, Divider, FormControlLabel, Paper, Popper, Stack, Checkbox, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

export type MapQuickFilterValues = {
  Status: string[];
  Observer: string[];
  CustomArea: string[];
  NetworkerStatus: string[];
};

export const MAP_QUICK_FILTER_DEFAULTS: MapQuickFilterValues = {
  Status: ['Active'],
  Observer: [],
  CustomArea: [],
  NetworkerStatus: [],
};

export const MAP_QUICK_FILTER_EMPTY: MapQuickFilterValues = {
  Status: [],
  Observer: [],
  CustomArea: [],
  NetworkerStatus: [],
};

interface QuickFilterOption {
  label: string;
  value: string;
}

interface MapQuickFiltersProps {
  values: MapQuickFilterValues;
  onChange: (values: MapQuickFilterValues) => void;
  observerOptions: QuickFilterOption[];
  customAreaOptions: QuickFilterOption[];
  networkerStatusOptions?: QuickFilterOption[];
  hasAdvancedFilters?: boolean;
  onResetAll?: () => void;
}

const STATUS_OPTIONS: QuickFilterOption[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Idle', value: 'Idle' },
  { label: 'Closed', value: 'Closed' },
  { label: 'PCR', value: 'PCR' },
  { label: 'PCA/Single Report', value: 'PCA/Single Report' },
];

const NETWORKER_OPTIONS: QuickFilterOption[] = [
  { label: 'True', value: 'TRUE' },
  { label: 'Partial', value: 'PARTIAL' },
  { label: 'False', value: 'FALSE' },
];

type FilterKey = keyof MapQuickFilterValues;

interface FilterChipConfig {
  key: FilterKey;
  label: string;
  options: QuickFilterOption[];
}

const FilterChip: React.FC<{
  config: FilterChipConfig;
  selected: string[];
  onChange: (key: FilterKey, values: string[]) => void;
}> = ({ config, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const toggle = (value: string) => {
    const next = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value];
    onChange(config.key, next);
  };

  const chipLabel = (() => {
    if (selected.length === 0) return config.label;
    if (selected.length === 1) {
      const opt = config.options.find(o => o.value === selected[0]);
      return `${config.label}: ${opt?.label ?? selected[0]}`;
    }
    return `${config.label}: ${selected.length} selected`;
  })();

  const isFiltered = selected.length > 0;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <Chip
          label={
            <Stack direction='row' alignItems='center' spacing={0.25}>
              <span>{chipLabel}</span>
              <ExpandMore
                sx={{ fontSize: 16, opacity: 0.7, ml: 0.25, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}
              />
            </Stack>
          }
          onClick={() => setOpen(prev => !prev)}
          onDelete={isFiltered ? () => onChange(config.key, []) : undefined}
          size='small'
          variant={isFiltered ? 'filled' : 'outlined'}
          color={isFiltered ? 'primary' : 'default'}
          sx={{
            cursor: 'pointer',
            fontWeight: isFiltered ? 600 : 400,
            fontSize: '0.78rem',
            height: 28,
            borderRadius: '14px',
            '& .MuiChip-label': { px: 1.25 },
            '& .MuiChip-deleteIcon': { fontSize: 14 },
            transition: 'all 0.15s',
          }}
        />
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          sx={{ zIndex: 1400 }}
          modifiers={[
            { name: 'flip', enabled: true },
            { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport' } },
          ]}
        >
          <Paper
            elevation={3}
            sx={{
              mt: 0.5,
              minWidth: 180,
              maxWidth: 260,
              maxHeight: 240,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box px={1.5} py={0.25} borderBottom='1px solid' sx={{ borderColor: 'divider', flexShrink: 0 }}>
              <Typography variant='caption' fontWeight={600} color='text.secondary' textTransform='uppercase' letterSpacing={0.5}>
                {config.label}
              </Typography>
            </Box>
            <Box px={0.5} py={0.25} sx={{ overflowY: 'auto', flexGrow: 1 }}>
              {config.options.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={selected.includes(opt.value)}
                      onChange={() => toggle(opt.value)}
                      size='small'
                      sx={{ py: 0.5, px: 1 }}
                    />
                  }
                  label={<Typography variant='body2'>{opt.label}</Typography>}
                  sx={{ display: 'flex', mx: 0, my: 0, px: 0.5 }}
                />
              ))}
            </Box>
            <Divider sx={{ flexShrink: 0, my: 0.25 }} />
            <Box px={1.5} py={0.25} display='flex' justifyContent='flex-end' sx={{ flexShrink: 0 }}>
              <Typography
                variant='caption'
                color={selected.length > 0 ? 'primary' : 'text.disabled'}
                sx={{ cursor: selected.length > 0 ? 'pointer' : 'default', fontWeight: 600 }}
                onClick={() => {
                  if (selected.length > 0) {
                    onChange(config.key, []);
                    setOpen(false);
                  }
                }}
              >
                Clear
              </Typography>
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

const MapQuickFilters: React.FC<MapQuickFiltersProps> = ({
  values,
  onChange,
  observerOptions,
  customAreaOptions,
  networkerStatusOptions,
  hasAdvancedFilters = false,
  onResetAll,
}) => {
  const handleChange = (key: FilterKey, selected: string[]) => {
    onChange({ ...values, [key]: selected });
  };

  const filterConfigs: FilterChipConfig[] = [
    { key: 'Status', label: 'Status', options: STATUS_OPTIONS },
    { key: 'Observer', label: 'Consultant', options: observerOptions },
    { key: 'CustomArea', label: 'Office', options: customAreaOptions },
    ...(networkerStatusOptions
      ? [{ key: 'NetworkerStatus' as FilterKey, label: 'Networker', options: networkerStatusOptions }]
      : [{ key: 'NetworkerStatus' as FilterKey, label: 'Networker', options: NETWORKER_OPTIONS }]),
  ];

  const isEmpty = (Object.keys(MAP_QUICK_FILTER_EMPTY) as FilterKey[]).every(key => values[key].length === 0) && !hasAdvancedFilters;

  return (
    <Stack direction='row' alignItems='center' spacing={0.75} flexWrap='wrap' rowGap={0.75}>
      {filterConfigs.map(config => (
        <FilterChip key={config.key} config={config} selected={values[config.key]} onChange={handleChange} />
      ))}
      {!isEmpty && <Box sx={{ alignSelf: 'center', height: 20, width: '1px', bgcolor: 'divider', mx: 0.5, flexShrink: 0 }} />}
      {!isEmpty && (
        <Chip
          label='Reset all'
          size='small'
          variant='outlined'
          onClick={() => (onResetAll ? onResetAll() : onChange(MAP_QUICK_FILTER_EMPTY))}
          sx={{
            alignSelf: 'center',
            fontSize: '0.75rem',
            height: 26,
            borderRadius: '13px',
            flexShrink: 0,
            cursor: 'pointer',
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover', borderColor: 'text.secondary', color: 'text.primary' },
          }}
        />
      )}
    </Stack>
  );
};

export default MapQuickFilters;
